import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Stack,
    Grid,
    Chip,
    IconButton,
    Tabs,
    Tab,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    InputAdornment,
    FormControlLabel,
    Link,
    Slider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import {
    Close as CloseIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Link as LinkIcon,
    ExpandMore as ExpandMoreIcon,
    Lock as LockIcon,
} from '@mui/icons-material';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import {
    IInfluencer,
    IPriceCard,
    IShippingAddress,
    IUnAvailabelDates,
    IDocument,
    ICategory,
} from '../../../redux/buzzbridge/types';
import { countries } from '../../../utils/countriesList';
import PictureUploader from '../../PictureUploader/PictureUploader';
import { CustomHeaderWithStatus } from '../../EsForm/CustomHeaderWithStatus';
import { ShippingAddress } from '../../ShippingAddress/ShippingAddress';
import { LANGUAGES } from '../../FindTalentsContainer/filterEnums';
import { CustomizedSwitches } from '../../Switch/Switch';
import { DropdownSelect } from '../../DropdownSelect/DropdownSelect';
import { CustomDatePicker } from '../../DatePicker/CustomDatePicker';
import CustomButton from '../../Button/CustomButton';

interface EditProfileModalProps {
    open: boolean;
    onClose: () => void;
    influencer: IInfluencer;
    onSave: (data: Partial<IInfluencer>) => void;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`edit-profile-tabpanel-${index}`}
            aria-labelledby={`edit-profile-tab-${index}`}
            {...other}>
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

const MAIN_CATEGORIES: string[] = [
    'Fashion',
    'Beauty & Skincare',
    'Fitness & Health',
    'Lifestyle',
    'Travel & Adventure',
    'Food & Cooking',
    'Technology & Gadgets',
    'Gaming',
    'Parenting & Family',
    'Education & Career',
    'Art & Design',
    'Music & Performance',
    'Finance & Investing',
    'Comedy & Entertainment',
    'Automotive',
    'Book Recommendations',
    'Sustainable Living',
    'Pet Influencers',
    'Spirituality & Mindfulness',
    'Minimalism',
    'DIY Home Renovation',
];

const SUBCATEGORIES: Record<string, string[]> = {
    Fashion: [
        'Streetwear',
        'Luxury Fashion',
        'Sustainable Fashion',
        'Outfit Ideas (OOTD)',
        'Styling Tips',
        'Fashion Hauls',
    ],
    'Beauty & Skincare': [
        'Makeup Tutorials',
        'Product Reviews',
        'Skincare Routines',
        'Beauty Hacks',
        'Before & After Transformations',
    ],
    'Fitness & Health': [
        'Home Workouts',
        'Gym Motivation',
        'Yoga & Meditation',
        'Nutrition & Diet',
        'Weight Loss Journeys',
        'Mental Wellness',
    ],
    Lifestyle: [
        'Daily Routines',
        'Home Aesthetics',
        'Productivity Tips',
        'Work-Life Balance',
        'Journaling',
        'Minimalist Living',
    ],
    'Travel & Adventure': [
        'Travel Vlogs',
        'Destination Guides',
        'Solo Travel',
        'Cultural Exploration',
        'Luxury Travel',
        'Budget Backpacking',
    ],
    'Food & Cooking': [
        'Recipe Videos',
        'ASMR Cooking',
        'Street Food Reviews',
        'Home Baking',
        'Vegan Recipes',
        'Food Challenges',
    ],
    'Technology & Gadgets': [
        'Unboxings',
        'Tech Reviews',
        'How-To Tutorials',
        'Mobile Accessories',
        'Coding & Dev Tools',
        'Productivity Apps',
    ],
    Gaming: ["Let's Play", 'Game Reviews', 'Streaming Clips', 'Esports', 'Speedruns', 'Game Modding'],
    'Parenting & Family': [
        'Family Vlogs',
        'Mom Life',
        'Parenting Hacks',
        'Pregnancy Tips',
        'Baby Care',
        'Family Activities',
    ],
    'Education & Career': [
        'Study Tips',
        'Career Advice',
        'Skill Development',
        'Online Learning',
        'Professional Growth',
        'Industry Insights',
    ],
    'Art & Design': [
        'Digital Art',
        'Traditional Art',
        'Design Tutorials',
        'Creative Process',
        'Art Challenges',
        'Design Inspiration',
    ],
    'Music & Performance': [
        'Music Covers',
        'Original Music',
        'Dance Videos',
        'Instrument Tutorials',
        'Behind the Scenes',
        'Performance Clips',
    ],
    'Finance & Investing': [
        'Investment Tips',
        'Budgeting Advice',
        'Cryptocurrency',
        'Stock Market',
        'Financial Planning',
        'Money Saving Hacks',
    ],
    'Comedy & Entertainment': [
        'Skits & Comedy',
        'Pranks',
        'Reactions',
        'Stand-up Comedy',
        'Funny Moments',
        'Entertainment News',
    ],
    Automotive: [
        'Car Reviews',
        'Modification Videos',
        'Driving Tips',
        'Car Maintenance',
        'Auto Shows',
        'Racing Content',
    ],
    'Book Recommendations': [
        'Book Reviews',
        'Reading Lists',
        'Book Clubs',
        'Author Interviews',
        'Literary Analysis',
        'Reading Challenges',
    ],
    'Sustainable Living': [
        'Zero Waste',
        'Eco-friendly Products',
        'Green Living Tips',
        'Environmental Awareness',
        'Sustainable Fashion',
        'Plant-based Living',
    ],
    'Pet Influencers': [
        'Pet Training',
        'Pet Care Tips',
        'Pet Adventures',
        'Pet Product Reviews',
        'Pet Health',
        'Pet Entertainment',
    ],
    'Spirituality & Mindfulness': [
        'Meditation Guides',
        'Spiritual Practices',
        'Mindfulness Tips',
        'Wellness Routines',
        'Spiritual Growth',
        'Peaceful Living',
    ],
    Minimalism: [
        'Decluttering',
        'Minimalist Lifestyle',
        'Simple Living',
        'Minimalist Design',
        'Digital Minimalism',
        'Minimalist Wardrobe',
    ],
    'DIY Home Renovation': [
        'Home Improvement',
        'DIY Projects',
        'Interior Design',
        'Home Decor',
        'Renovation Tips',
        'Before & After',
    ],
};

function findParentCategory(value: string): string | null {
    for (const [category, subcategories] of Object.entries(SUBCATEGORIES)) {
        if (subcategories.includes(value)) {
            return category;
        }
    }
    return null; // Return null if not found
}

function getAllExceptParentAndChildren(parent: string): Record<string, string[]> {
    const result: Record<string, string[]> = {};

    for (const [key, value] of Object.entries(SUBCATEGORIES)) {
        if (key !== parent) {
            result[key] = value;
        }
    }

    return result;
}

// Extended document type to handle both existing documents and newly uploaded files
interface ExtendedDocument extends IDocument {
    size?: number;
}

export const EditProfileModal: React.FC<EditProfileModalProps> = ({ open, onClose, influencer, onSave }) => {
    const { themeStyle } = useThemeContext();
    const [activeTab, setActiveTab] = useState(0);
    const [mainCategory] = useState(MAIN_CATEGORIES);
    const [subCategoriesMapped, setSubCategories] = useState<Record<string, string[]>>(SUBCATEGORIES);
    const [formData, setFormData] = useState<Partial<IInfluencer>>({
        ...influencer,
    });

    // Store original data for comparison
    const [originalData] = useState<Partial<IInfluencer>>({
        ...influencer,
    });

    // Profile picture state
    const [profilePicture, setProfilePicture] = useState<string | undefined>(influencer.profile_picture);
    const [originalProfilePicture] = useState<string | undefined>(influencer.profile_picture);

    // Shipping location state
    const [shippingLocation, setShippingLocation] = useState<IShippingAddress | undefined>(influencer.shipping_address);
    const [originalShippingLocation] = useState<IShippingAddress | undefined>(influencer.shipping_address);

    // Document state - use ExtendedDocument type
    const [uploadedDocuments, setUploadedDocuments] = useState<ExtendedDocument[]>(
        (influencer.documents || []) as ExtendedDocument[],
    );
    const [originalDocuments] = useState<ExtendedDocument[]>((influencer.documents || []) as ExtendedDocument[]);

    // Portfolio state
    const [newPortfolioLink, setNewPortfolioLink] = useState<string>('');

    // Category selection state
    const [selectedMainCategories, setSelectedMainCategories] = useState<string[]>(
        influencer.categories?.map(cat => cat.main) || [],
    );
    const [selectedSubCategories, setSelectedSubCategories] = useState<string[]>(
        influencer.categories?.filter(cat => cat.sub).map(cat => cat.sub!) || [],
    );
    const [originalCategories] = useState<ICategory[]>(influencer.categories || []);

    // Unavailable date state
    const [newUnavailableDate, setNewUnavailableDate] = useState<IUnAvailabelDates>({
        from: '',
        to: '',
        reason: '',
    });

    useEffect(() => {
        // Update form data when influencer prop changes
        setFormData(influencer);
        setProfilePicture(influencer.profile_picture);
        setShippingLocation(influencer.shipping_address);
        setUploadedDocuments((influencer.documents || []) as ExtendedDocument[]);
        setSelectedMainCategories(influencer.categories?.map(cat => cat.main) || []);
        setSelectedSubCategories(influencer.categories?.filter(cat => cat.sub).map(cat => cat.sub!) || []);
    }, [influencer]);

    // Helper function to check if values are different
    const hasChanged = (current: any, original: any): boolean => {
        if (Array.isArray(current) && Array.isArray(original)) {
            if (current.length !== original.length) return true;
            return current.some((item, index) => JSON.stringify(item) !== JSON.stringify(original[index]));
        }
        return JSON.stringify(current) !== JSON.stringify(original);
    };

    // Helper function to get only changed fields
    const getChangedFields = (current: any, original: any): any => {
        const changes: any = {};

        Object.keys(current).forEach(key => {
            if (hasChanged(current[key], original[key])) {
                changes[key] = current[key];
            }
        });

        return changes;
    };

    // Helper function to check if there are any changes
    const hasAnyChanges = (): boolean => {
        const currentData = {
            ...formData,
            profile_picture: profilePicture,
            shipping_address: shippingLocation,
            documents: uploadedDocuments,
            categories: [
                ...selectedSubCategories.map(sub => {
                    const parent = findParentCategory(sub);
                    const obj = { main: '', sub };
                    if (!!parent) {
                        obj.main = parent;
                    }
                    return obj;
                }),
            ],
        };

        const originalDataForComparison = {
            ...originalData,
            profile_picture: originalProfilePicture,
            shipping_address: originalShippingLocation,
            documents: originalDocuments,
            categories: originalCategories,
        };

        const changedFields = getChangedFields(currentData, originalDataForComparison);
        return Object.keys(changedFields).length > 0;
    };

    // Collaboration types and payment methods
    const COLLABORATION_TYPES = [
        'paid',
        'gifted',
        'product exchange',
        'commission based',
        'affiliate',
        'sponsored',
        'brand ambassador',
        'influencer marketing',
        'content creation',
        'event appearance',
    ];

    const PAYMENT_METHODS = [
        'paypal',
        'wire transfer',
        'cryptocurrency',
        'stock options',
        'bank transfer',
        'stripe',
        'venmo',
        'cash app',
        'zelle',
        'check',
    ];

    // Define the list of keys using `as const` for strict typing
    const CONTENT_CATEGORIES = [
        'post',
        'story',
        'reel',
        'video',
        'live stream',
        'sponsored post',
        'product review',
        'brand mention',
        'event coverage',
        'bundle',
    ] as const satisfies ReadonlyArray<keyof IPriceCard>;

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const handleInputChange = (field: keyof IInfluencer, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSelectChange = (field: keyof IInfluencer) => (event: SelectChangeEvent) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value,
        }));
    };

    const handleArrayFieldChange = (field: keyof IInfluencer, value: string[]) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleRemoveArrayItem = (field: keyof IInfluencer, itemToRemove: string) => {
        const currentArray = (formData[field] as string[]) || [];
        handleArrayFieldChange(
            field,
            currentArray.filter(item => item !== itemToRemove),
        );
    };

    const handleProfilePictureChange = (image: any, rawFile?: any) => {
        setProfilePicture(image);
        if (image) {
            setFormData(prev => ({
                ...prev,
                profile_picture: image,
                rawProfile: rawFile,
            }));
        }
    };

    const handleLocationSelect = (address?: IShippingAddress) => {
        if (address) {
            setShippingLocation(address);
            setFormData(prev => ({
                ...prev,
                shipping_address: address,
            }));
        }
    };

    const handleAddUnavailableDate = () => {
        if (newUnavailableDate.from && newUnavailableDate.to && newUnavailableDate.reason) {
            const currentDates = (formData.unavailable_dates as any) || [];
            const datesArray = Array.isArray(currentDates) ? currentDates : [];
            handleInputChange('unavailable_dates', [...datesArray, newUnavailableDate]);
            setNewUnavailableDate({
                from: '',
                to: '',
                reason: '',
            });
        }
    };

    const handleRemoveUnavailableDate = (index: number) => {
        const currentDates = (formData.unavailable_dates as any) || [];
        const datesArray = Array.isArray(currentDates) ? currentDates : [];
        const updatedDates = datesArray.filter((_: any, i: number) => i !== index);
        handleInputChange('unavailable_dates', updatedDates);
    };

    const handleAddPortfolioLink = () => {
        if (newPortfolioLink.trim()) {
            const currentPortfolio = formData.portfolio || [];
            if (!currentPortfolio.includes(newPortfolioLink.trim())) {
                handleArrayFieldChange('portfolio', [...currentPortfolio, newPortfolioLink.trim()]);
                setNewPortfolioLink('');
            }
        }
    };

    const handleRemovePortfolioLink = (linkToRemove: string) => {
        const currentPortfolio = formData.portfolio || [];
        handleArrayFieldChange(
            'portfolio',
            currentPortfolio.filter(link => link !== linkToRemove),
        );
    };

    const handleDocumentUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files) {
            const newFiles: ExtendedDocument[] = Array.from(files).map(file => ({
                url: URL.createObjectURL(file),
                name: file.name,
                format: file.type,
                size: file.size, // Include size for newly uploaded files
                file: file,
            }));

            setUploadedDocuments(prev => {
                const updatedDocs = [...prev, ...newFiles];
                setFormData(prevData => ({
                    ...prevData,
                    documents: updatedDocs,
                }));
                return updatedDocs;
            });
        }
    };

    const handleRemoveDocument = (index: number) => {
        setUploadedDocuments(prev => {
            const updatedDocs = prev.filter((_, i) => i !== index);
            setFormData(prevData => ({
                ...prevData,
                documents: updatedDocs,
            }));
            return updatedDocs;
        });
    };

    const handleSave = () => {
        // Create categories array from main and sub categories
        const categories: ICategory[] = [
            ...selectedSubCategories.map(sub => {
                const parent = findParentCategory(sub);
                const obj = { main: '', sub };
                if (!!parent) {
                    obj.main = parent;
                }
                return obj;
            }),
        ];

        // Collect all current values
        const currentData = {
            ...formData,
            profile_picture: profilePicture,
            shipping_address: shippingLocation,
            documents: uploadedDocuments,
            categories: categories,
        };

        // Get only the fields that have changed
        const changedFields = getChangedFields(currentData, {
            ...originalData,
            profile_picture: originalProfilePicture,
            shipping_address: originalShippingLocation,
            documents: originalDocuments,
            categories: originalCategories,
        });

        // Only send changed fields to the parent component
        onSave(changedFields);
    };

    const commonTextFieldProps = {
        sx: {
            '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: theme[themeStyle].form_divider },
                '&:hover fieldset': { borderColor: theme[themeStyle].highlight },
                '&.Mui-focused fieldset': { borderColor: theme[themeStyle].highlight },
            },
            '& .MuiInputLabel-root': {
                color: theme[themeStyle].form_label,
                fontFamily: FONT_ENUM.MAIN_MEDIUM,
            },
            '& .MuiInputLabel-root.Mui-focused': { color: theme[themeStyle].highlight },
            '& .MuiInputBase-input': {
                color: theme[themeStyle].primary_white,
                fontFamily: FONT_ENUM.MAIN_MEDIUM,
            },
            '& ::placeholder': {
                color: theme[themeStyle].placeholderGray,
                fontFamily: FONT_ENUM.REGULAR_HEADER,
            },
        },
    };

    const commonSelectProps = {
        sx: {
            '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: theme[themeStyle].form_divider },
                '&:hover fieldset': { borderColor: theme[themeStyle].highlight },
                '&.Mui-focused fieldset': { borderColor: theme[themeStyle].highlight },
            },
            '& .MuiInputLabel-root': {
                color: theme[themeStyle].form_label,
                fontFamily: FONT_ENUM.MAIN_MEDIUM,
            },
            '& .MuiInputLabel-root.Mui-focused': { color: theme[themeStyle].highlight },
            '& .MuiSelect-select': {
                color: theme[themeStyle].primary_white,
                fontFamily: FONT_ENUM.MAIN_MEDIUM,
            },
        },
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            fullScreen={window.innerWidth < 768}
            PaperProps={{
                sx: {
                    backgroundColor: theme[themeStyle].secondaryBg,
                    color: theme[themeStyle].primary_white,
                    borderRadius: window.innerWidth < 768 ? 0 : 2,
                },
            }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: `1px solid ${theme[themeStyle].form_divider}`,
                    p: 2,
                }}>
                <Typography
                    variant="h6"
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        color: theme[themeStyle].primary_white,
                    }}>
                    Edit Bio
                </Typography>
                <IconButton
                    onClick={onClose}
                    sx={{
                        color: theme[themeStyle].primary_white,
                        '&:hover': { backgroundColor: theme[themeStyle].form_divider },
                    }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ borderBottom: `1px solid ${theme[themeStyle].form_divider}` }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{
                            '& .MuiTab-root': {
                                color: theme[themeStyle].form_label,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                textTransform: 'none',
                                minWidth: 'auto',
                                px: 2,
                                '&.Mui-selected': {
                                    color: theme[themeStyle].highlight,
                                },
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: theme[themeStyle].highlight,
                            },
                            '& .MuiTabs-scrollButtons': {
                                color: theme[themeStyle].form_label,
                                '&.Mui-disabled': {
                                    opacity: 0.3,
                                },
                            },
                        }}>
                        <Tab label="Basic Info" />
                        <Tab label="Categories" />
                        <Tab label="Languages" />
                        <Tab label="Preferences" />
                        <Tab label="Media" />
                        <Tab label="Social Platforms" />
                    </Tabs>
                </Box>
                <Box p={2}>
                    <TabPanel value={activeTab} index={0}>
                        <Stack spacing={3}>
                            {/* Profile Picture */}
                            <Box>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: theme[themeStyle].primary_white,
                                        mb: 2,
                                    }}>
                                    Profile Picture
                                </Typography>
                                <PictureUploader
                                    initialImageSrc={
                                        influencer.profile_picture ?? influencer.instagram?.profile_picture!
                                    }
                                    onChange={handleProfilePictureChange}
                                    onRemove={() => handleProfilePictureChange(undefined)}
                                    sx={{ maxWidth: 200 }}
                                />
                            </Box>

                            {/* Basic Information */}
                            <Grid
                                container
                                spacing={2}
                                width={'100%'}
                                sx={{
                                    '.MuiGrid-item': {
                                        px: 0.5,
                                    },
                                }}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Name"
                                        fullWidth
                                        value={formData.name || ''}
                                        onChange={e => handleInputChange('name', e.target.value)}
                                        {...commonTextFieldProps}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Date of Birth"
                                        fullWidth
                                        type="date"
                                        value={formData.date_of_birth || ''}
                                        onChange={e => handleInputChange('date_of_birth', e.target.value)}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        {...commonTextFieldProps}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                    }}>
                                    <FormControl
                                        fullWidth
                                        sx={{
                                            '&.Mui-focused': { color: theme[themeStyle].highlight },
                                        }}>
                                        <InputLabel
                                            sx={{
                                                color: theme[themeStyle].form_label,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            }}>
                                            Country
                                        </InputLabel>
                                        <Select
                                            value={formData.country || ''}
                                            onChange={handleSelectChange('country')}
                                            label="Country"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '8px',
                                                    '& fieldset': {
                                                        borderColor: theme[themeStyle].placeholderGray,
                                                        borderWidth: '1px',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: theme[themeStyle].highlight,
                                                        borderWidth: '1px',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: theme[themeStyle].highlight,
                                                        borderWidth: '2px',
                                                    },
                                                },
                                                '& .MuiSelect-select': {
                                                    color: theme[themeStyle].primary_white,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    color: theme[themeStyle].primary_white,
                                                },
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        backgroundColor: theme[themeStyle].dropdown_black,
                                                        border: `1px solid ${theme[themeStyle].form_divider}`,
                                                        borderRadius: '8px',
                                                        '& .MuiMenuItem-root': {
                                                            color: theme[themeStyle].primary_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            '&:hover': {
                                                                backgroundColor: theme[themeStyle].form_divider,
                                                            },
                                                            '&.Mui-selected': {
                                                                backgroundColor: theme[themeStyle].highlight,
                                                                color: theme[themeStyle].primary_white,
                                                                '&:hover': {
                                                                    backgroundColor: theme[themeStyle].highlight,
                                                                },
                                                            },
                                                        },
                                                    },
                                                },
                                            }}>
                                            {countries.map(country => (
                                                <MenuItem key={country.code} value={country.name}>
                                                    {country.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                    }}>
                                    <FormControl
                                        fullWidth
                                        sx={{
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme[themeStyle].form_divider,
                                            },
                                            '&:hover fieldset': { borderColor: theme[themeStyle].highlight },
                                        }}>
                                        <InputLabel
                                            sx={{
                                                color: theme[themeStyle].form_label,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                '&.Mui-focused': { color: theme[themeStyle].highlight },
                                            }}>
                                            Nationality
                                        </InputLabel>
                                        <Select
                                            value={formData.nationality || ''}
                                            onChange={handleSelectChange('nationality')}
                                            label="Nationality"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '8px',
                                                    '& fieldset': {
                                                        borderColor: theme[themeStyle].form_divider,
                                                        borderWidth: '1px',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: theme[themeStyle].highlight,
                                                        borderWidth: '1px',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: theme[themeStyle].highlight,
                                                        borderWidth: '2px',
                                                    },
                                                },
                                                '& .MuiSelect-select': {
                                                    color: theme[themeStyle].primary_white,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    color: theme[themeStyle].primary_white,
                                                },
                                                ...commonSelectProps,
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        backgroundColor: theme[themeStyle].dropdown_black,
                                                        border: `1px solid ${theme[themeStyle].form_divider}`,
                                                        borderRadius: '8px',
                                                        '& .MuiMenuItem-root': {
                                                            color: theme[themeStyle].dropdown_text,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            '&:hover': {
                                                                backgroundColor: theme[themeStyle].form_divider,
                                                            },
                                                            '&.Mui-selected': {
                                                                backgroundColor: theme[themeStyle].highlight,
                                                                color: theme[themeStyle].primary_white,
                                                                '&:hover': {
                                                                    backgroundColor: theme[themeStyle].highlight,
                                                                },
                                                            },
                                                        },
                                                    },
                                                },
                                            }}>
                                            {countries.map(country => (
                                                <MenuItem key={country.code} value={country.name}>
                                                    {country.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Bio"
                                        fullWidth
                                        multiline
                                        rows={4}
                                        value={formData.bio || ''}
                                        onChange={e => handleInputChange('bio', e.target.value)}
                                        {...commonTextFieldProps}
                                    />
                                </Grid>
                            </Grid>

                            {/* Shipping Location */}
                            <Box>
                                <CustomHeaderWithStatus
                                    headerTitle="Shipping Location"
                                    themeStyle={themeStyle}
                                    field="shipping_address"
                                    state={formData}
                                    headerToolTip="This address here will be used to ship products to yuo. Ensure that it is accurate"
                                    style={{
                                        marginBottom: '16px',
                                    }}
                                />
                                <ShippingAddress value={shippingLocation} onChange={handleLocationSelect} />
                            </Box>
                        </Stack>
                    </TabPanel>

                    <TabPanel value={activeTab} index={1}>
                        <Stack spacing={3}>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                Content Categories
                            </Typography>

                            {/* Main Category Selection */}
                            <Box>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].form_label,
                                        mb: 1,
                                    }}>
                                    Main Categories (Max 3)
                                </Typography>

                                {/* Selected Main Categories Chips */}
                                {selectedMainCategories.length > 0 && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                        {selectedMainCategories.map((category, index) => (
                                            <Chip
                                                key={index}
                                                label={category}
                                                onDelete={() => {
                                                    const updated = selectedMainCategories.filter(
                                                        (_, i) => i !== index,
                                                    );
                                                    setSelectedMainCategories(updated);
                                                }}
                                                sx={{
                                                    backgroundColor: theme[themeStyle].highlight,
                                                    color: theme[themeStyle].black,
                                                    '& .MuiChip-deleteIcon': {
                                                        color: theme[themeStyle].black,
                                                    },
                                                }}
                                            />
                                        ))}
                                    </Box>
                                )}

                                <FormControl
                                    fullWidth
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover fieldset': { borderColor: theme[themeStyle].highlight },
                                    }}>
                                    <InputLabel
                                        sx={{
                                            color: theme[themeStyle].form_label,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            '&.Mui-focused': { color: theme[themeStyle].highlight },
                                        }}>
                                        Select Main Categories
                                    </InputLabel>
                                    <Select
                                        multiple
                                        value={selectedMainCategories}
                                        onChange={event => {
                                            const value = event.target.value as string[];
                                            // Limit to 3 selections
                                            const limitedValue = value.slice(0, 3);
                                            setSelectedMainCategories(limitedValue);
                                            // Clear sub categories when main categories change
                                            setSelectedSubCategories([]);
                                        }}
                                        label="Select Main Categories"
                                        renderValue={selected => (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {(selected as string[]).map(value => (
                                                    <Chip
                                                        key={value}
                                                        label={value}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: theme[themeStyle].highlight,
                                                            color: theme[themeStyle].black,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                                '& fieldset': {
                                                    borderColor: theme[themeStyle].form_divider,
                                                    borderWidth: '1px',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: theme[themeStyle].highlight,
                                                    borderWidth: '1px',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: theme[themeStyle].highlight,
                                                    borderWidth: '2px',
                                                },
                                            },
                                            '& .MuiSelect-select': {
                                                color: theme[themeStyle].primary_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            },
                                            '& .MuiSvgIcon-root': {
                                                color: theme[themeStyle].primary_white,
                                            },
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    backgroundColor: theme[themeStyle].dropdown_black,
                                                    border: `1px solid ${theme[themeStyle].form_divider}`,
                                                    borderRadius: '8px',
                                                    '& .MuiMenuItem-root': {
                                                        color: theme[themeStyle].primary_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].form_divider,
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: theme[themeStyle].highlight,
                                                            color: theme[themeStyle].primary_white,
                                                            '&:hover': {
                                                                backgroundColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                        }}>
                                        {mainCategory.map(category => (
                                            <MenuItem key={category} value={category}>
                                                {category}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Sub Category Selection */}
                            {selectedMainCategories.length > 0 && (
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].form_label,
                                            mb: 1,
                                        }}>
                                        Sub Categories (Max 3)
                                    </Typography>

                                    {/* Selected Sub Categories Chips */}
                                    {selectedSubCategories.length > 0 && (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                            {selectedSubCategories.map((category, index) => (
                                                <Chip
                                                    key={index}
                                                    label={category}
                                                    onDelete={() => {
                                                        const updated = selectedSubCategories.filter(
                                                            (_, i) => i !== index,
                                                        );
                                                        setSelectedSubCategories(updated);
                                                    }}
                                                    sx={{
                                                        backgroundColor: theme[themeStyle].highlight,
                                                        color: theme[themeStyle].black,
                                                        '& .MuiChip-deleteIcon': {
                                                            color: theme[themeStyle].black,
                                                        },
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    )}

                                    <FormControl
                                        fullWidth
                                        sx={{
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme[themeStyle].form_divider,
                                            },
                                            '&:hover fieldset': { borderColor: theme[themeStyle].highlight },
                                        }}>
                                        <InputLabel
                                            sx={{
                                                color: theme[themeStyle].form_label,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                '&.Mui-focused': { color: theme[themeStyle].highlight },
                                            }}>
                                            Select Sub Categories
                                        </InputLabel>
                                        <Select
                                            multiple
                                            value={selectedSubCategories}
                                            onChange={event => {
                                                const value = event.target.value as string[];
                                                // Limit to 3 selections
                                                const limitedValue = value.slice(0, 3);
                                                setSelectedSubCategories(limitedValue);
                                                value.forEach(val => {
                                                    const parent = findParentCategory(val);
                                                    if (!!parent) {
                                                        const children = getAllExceptParentAndChildren(parent);
                                                        setSubCategories(children);
                                                    }
                                                });
                                            }}
                                            label="Select Sub Categories"
                                            renderValue={selected => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {(selected as string[]).map(value => (
                                                        <Chip
                                                            key={value}
                                                            label={value}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: theme[themeStyle].highlight,
                                                                color: theme[themeStyle].black,
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            }}
                                                        />
                                                    ))}
                                                </Box>
                                            )}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '8px',
                                                    '& fieldset': {
                                                        borderColor: theme[themeStyle].form_divider,
                                                        borderWidth: '1px',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: theme[themeStyle].highlight,
                                                        borderWidth: '1px',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: theme[themeStyle].highlight,
                                                        borderWidth: '2px',
                                                    },
                                                },
                                                '& .MuiSelect-select': {
                                                    color: theme[themeStyle].primary_white,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    color: theme[themeStyle].primary_white,
                                                },
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        backgroundColor: theme[themeStyle].dropdown_black,
                                                        border: `1px solid ${theme[themeStyle].form_divider}`,
                                                        borderRadius: '8px',
                                                        '& .MuiMenuItem-root': {
                                                            color: theme[themeStyle].primary_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            '&:hover': {
                                                                backgroundColor: theme[themeStyle].form_divider,
                                                            },
                                                            '&.Mui-selected': {
                                                                backgroundColor: theme[themeStyle].highlight,
                                                                color: theme[themeStyle].primary_white,
                                                                '&:hover': {
                                                                    backgroundColor: theme[themeStyle].highlight,
                                                                },
                                                            },
                                                        },
                                                    },
                                                },
                                            }}>
                                            {(() => {
                                                const subCategories = selectedMainCategories
                                                    .map(main => subCategoriesMapped[main] || [])
                                                    .flat();

                                                return subCategories.map(category => (
                                                    <MenuItem key={category} value={category}>
                                                        {category}
                                                    </MenuItem>
                                                ));
                                            })()}
                                        </Select>
                                    </FormControl>
                                </Box>
                            )}

                            {/* Add Category Button */}
                            {selectedMainCategories.length > 0 && (
                                <Box>
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            const newCategories = selectedMainCategories.map(main => ({ main }));
                                            const currentCategories = formData.categories || [];
                                            const categoryExists = newCategories.some(newCat =>
                                                currentCategories.some(existingCat => existingCat.main === newCat.main),
                                            );

                                            if (!categoryExists) {
                                                handleInputChange('categories', [
                                                    ...currentCategories,
                                                    ...newCategories,
                                                ]);
                                                // Clear selections after adding
                                                setSelectedMainCategories([]);
                                                setSelectedSubCategories([]);
                                            }
                                        }}
                                        sx={{
                                            borderColor: theme[themeStyle].highlight,
                                            color: theme[themeStyle].highlight,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            textTransform: 'none',
                                            '&:hover': {
                                                borderColor: theme[themeStyle].highlight,
                                                backgroundColor: theme[themeStyle].form_divider,
                                            },
                                        }}>
                                        Add Categories
                                    </Button>
                                </Box>
                            )}
                        </Stack>
                    </TabPanel>

                    <TabPanel value={activeTab} index={2}>
                        <Stack spacing={3}>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                Languages (Max 5)
                            </Typography>

                            {/* Selected Languages Chips */}
                            {(formData.languages_spoken || []).length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                    {(formData.languages_spoken || []).map((language, index) => (
                                        <Chip
                                            key={index}
                                            label={language}
                                            onDelete={() => handleRemoveArrayItem('languages_spoken', language)}
                                            sx={{
                                                backgroundColor: theme[themeStyle].highlight,
                                                color: theme[themeStyle].black,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                '& .MuiChip-deleteIcon': {
                                                    color: theme[themeStyle].black,
                                                },
                                            }}
                                        />
                                    ))}
                                </Box>
                            )}

                            <FormControl
                                fullWidth
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].form_divider,
                                    },
                                    '&:hover fieldset': { borderColor: theme[themeStyle].highlight },
                                }}>
                                <InputLabel
                                    sx={{
                                        color: theme[themeStyle].form_label,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        '&.Mui-focused': { color: theme[themeStyle].highlight },
                                    }}>
                                    Select Languages
                                </InputLabel>
                                <Select
                                    multiple
                                    value={formData.languages_spoken || []}
                                    onChange={event => {
                                        const value = event.target.value as string[];
                                        // Limit to 5 selections
                                        const limitedValue = value.slice(0, 5);
                                        handleArrayFieldChange('languages_spoken', limitedValue);
                                    }}
                                    label="Select Languages"
                                    renderValue={selected => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {(selected as string[]).map(value => (
                                                <Chip
                                                    key={value}
                                                    label={value}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: theme[themeStyle].highlight,
                                                        color: theme[themeStyle].black,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    )}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                            '& fieldset': {
                                                borderColor: theme[themeStyle].form_divider,
                                                borderWidth: '1px',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme[themeStyle].highlight,
                                                borderWidth: '1px',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme[themeStyle].highlight,
                                                borderWidth: '2px',
                                            },
                                        },
                                        '& .MuiSelect-select': {
                                            color: theme[themeStyle].primary_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].primary_white,
                                        },
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].dropdown_black,
                                                border: `1px solid ${theme[themeStyle].form_divider}`,
                                                borderRadius: '8px',
                                                maxHeight: 300,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].primary_white,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].form_divider,
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme[themeStyle].highlight,
                                                        color: theme[themeStyle].primary_white,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].highlight,
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    }}>
                                    {LANGUAGES.map(language => (
                                        <MenuItem key={language} value={language}>
                                            {language.charAt(0).toUpperCase() + language.slice(1)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>
                    </TabPanel>

                    {/* Preferences Tab */}
                    <TabPanel value={activeTab} index={3}>
                        <Stack spacing={3}>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                Preferences
                            </Typography>

                            {/* Collaboration Types */}
                            <Box>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].form_label,
                                        mb: 1,
                                    }}>
                                    Preferred Collaboration Types
                                </Typography>
                                <DropdownSelect
                                    label=""
                                    data={COLLABORATION_TYPES}
                                    placeHolder="Select collaboration type"
                                    onOptionSelected={(value: string) => {
                                        const currentTypes = formData.preferred_collab_types || [];
                                        if (value && !currentTypes.includes(value)) {
                                            handleArrayFieldChange('preferred_collab_types', [...currentTypes, value]);
                                        }
                                    }}
                                    sx={{
                                        backgroundColor: theme[themeStyle].gray_text,
                                        color: theme[themeStyle].lightWhite,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                    }}
                                />
                                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {(formData.preferred_collab_types || []).map((type, index) => (
                                        <Chip
                                            key={index}
                                            label={type}
                                            onDelete={() => handleRemoveArrayItem('preferred_collab_types', type)}
                                            sx={{
                                                backgroundColor: theme[themeStyle].highlight,
                                                color: theme[themeStyle].black,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                '& .MuiChip-deleteIcon': {
                                                    color: theme[themeStyle].black,
                                                },
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>

                            {/* Availability Switches */}
                            <Box>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].form_label,
                                        mb: 2,
                                    }}>
                                    Availability
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <FormControlLabel
                                            control={
                                                <CustomizedSwitches
                                                    switchProps={{
                                                        checked: formData.available_for_events || false,
                                                        onChange: e =>
                                                            handleInputChange('available_for_events', e.target.checked),
                                                    }}
                                                />
                                            }
                                            label={
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        color: theme[themeStyle].form_label,
                                                    }}>
                                                    Available for Events
                                                </Typography>
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControlLabel
                                            control={
                                                <CustomizedSwitches
                                                    switchProps={{
                                                        checked: formData.available_for_long_term_collabs || false,
                                                        onChange: e =>
                                                            handleInputChange(
                                                                'available_for_long_term_collabs',
                                                                e.target.checked,
                                                            ),
                                                    }}
                                                />
                                            }
                                            label={
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        color: theme[themeStyle].form_label,
                                                    }}>
                                                    Available for Long-term Collaborations
                                                </Typography>
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControlLabel
                                            control={
                                                <CustomizedSwitches
                                                    switchProps={{
                                                        checked: formData.availability !== false,
                                                        onChange: e =>
                                                            handleInputChange('availability', e.target.checked),
                                                    }}
                                                />
                                            }
                                            label={
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        color: theme[themeStyle].form_label,
                                                    }}>
                                                    Generally Available
                                                </Typography>
                                            }
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Preferred Payment Method */}
                            <Box>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].form_label,
                                        mb: 1,
                                    }}>
                                    Preferred Payment Method
                                </Typography>
                                <FormControl
                                    fullWidth
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover fieldset': { borderColor: theme[themeStyle].highlight },
                                    }}>
                                    <Select
                                        value={formData.preferred_payment_method || ''}
                                        onChange={e => handleInputChange('preferred_payment_method', e.target.value)}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                                '& fieldset': {
                                                    borderColor: theme[themeStyle].form_divider,
                                                    borderWidth: '1px',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: theme[themeStyle].highlight,
                                                    borderWidth: '1px',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: theme[themeStyle].highlight,
                                                    borderWidth: '2px',
                                                },
                                            },
                                            '& .MuiSelect-select': {
                                                color: theme[themeStyle].primary_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            },
                                            '& .MuiSvgIcon-root': {
                                                color: theme[themeStyle].primary_white,
                                            },
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    backgroundColor: theme[themeStyle].dropdown_black,
                                                    border: `1px solid ${theme[themeStyle].form_divider}`,
                                                    borderRadius: '8px',
                                                    '& .MuiMenuItem-root': {
                                                        color: theme[themeStyle].primary_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].form_divider,
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: theme[themeStyle].highlight,
                                                            color: theme[themeStyle].primary_white,
                                                            '&:hover': {
                                                                backgroundColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                        }}>
                                        <MenuItem value="">
                                            <Typography sx={{ color: theme[themeStyle].form_label }}>
                                                Select payment method
                                            </Typography>
                                        </MenuItem>
                                        {PAYMENT_METHODS.map(method => (
                                            <MenuItem key={method} value={method}>
                                                <Typography sx={{ color: theme[themeStyle].primary_white }}>
                                                    {method.charAt(0).toUpperCase() + method.slice(1)}
                                                </Typography>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Unavailable Dates */}
                            <Box>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].form_label,
                                        mb: 2,
                                    }}>
                                    Unavailable Dates
                                </Typography>

                                {/* Add new unavailable date range */}
                                <Box
                                    sx={{
                                        mb: 2,
                                        p: 2,
                                        border: `1px solid ${theme[themeStyle].form_divider}`,
                                        borderRadius: 2,
                                        backgroundColor: theme[themeStyle].form_background,
                                    }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    color: theme[themeStyle].form_label,
                                                    mb: 1,
                                                }}>
                                                From Date
                                            </Typography>
                                            <CustomDatePicker
                                                value={newUnavailableDate.from || ''}
                                                onChange={(date: string) =>
                                                    setNewUnavailableDate(prev => ({ ...prev, from: date }))
                                                }
                                                sx={{
                                                    backgroundColor: theme[themeStyle].form_fields,
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    color: theme[themeStyle].form_label,
                                                    mb: 1,
                                                }}>
                                                To Date
                                            </Typography>
                                            <CustomDatePicker
                                                value={newUnavailableDate.to || ''}
                                                onChange={(date: string) =>
                                                    setNewUnavailableDate(prev => ({ ...prev, to: date }))
                                                }
                                                minDate={newUnavailableDate.from}
                                                sx={{
                                                    backgroundColor: theme[themeStyle].form_fields,
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    color: theme[themeStyle].form_label,
                                                    mb: 1,
                                                }}>
                                                Reason
                                            </Typography>
                                            <TextField
                                                value={newUnavailableDate.reason || ''}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                    setNewUnavailableDate(prev => ({ ...prev, reason: e.target.value }))
                                                }
                                                placeholder="Enter reason for unavailability"
                                                multiline
                                                rows={2}
                                                fullWidth
                                                sx={{
                                                    backgroundColor: theme[themeStyle].form_fields,
                                                    '& .MuiOutlinedInput-root': {
                                                        color: theme[themeStyle].primary_white,
                                                        '& fieldset': {
                                                            borderColor: theme[themeStyle].form_divider,
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: theme[themeStyle].highlight,
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: theme[themeStyle].highlight,
                                                        },
                                                    },
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Button
                                                onClick={handleAddUnavailableDate}
                                                disabled={
                                                    !newUnavailableDate.from ||
                                                    !newUnavailableDate.to ||
                                                    !newUnavailableDate.reason
                                                }
                                                sx={{
                                                    backgroundColor: theme[themeStyle].highlight,
                                                    color: theme[themeStyle].primary_white,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    textTransform: 'none',
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].highlight,
                                                        opacity: 0.9,
                                                    },
                                                    '&:disabled': {
                                                        backgroundColor: theme[themeStyle].form_divider,
                                                        color: theme[themeStyle].placeholderGray,
                                                    },
                                                }}>
                                                Add Unavailable Date Range
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Box>

                                {/* Display existing unavailable dates */}
                                {(() => {
                                    const currentDates = (formData.unavailable_dates as any) || [];
                                    const datesArray = Array.isArray(currentDates) ? currentDates : [];
                                    return datesArray.map((dateRange: any, index: number) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                mb: 2,
                                                p: 2,
                                                border: `1px solid ${theme[themeStyle].form_divider}`,
                                                borderRadius: 2,
                                                backgroundColor: theme[themeStyle].form_background,
                                            }}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'flex-start',
                                                    mb: 1,
                                                }}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        color: theme[themeStyle].primary_white,
                                                    }}>
                                                    {new Date(dateRange.from).toLocaleDateString()} -{' '}
                                                    {new Date(dateRange.to).toLocaleDateString()}
                                                </Typography>
                                                <Button
                                                    onClick={() => handleRemoveUnavailableDate(index)}
                                                    sx={{
                                                        color: theme[themeStyle].primary_red,
                                                        minWidth: 'auto',
                                                        p: 0,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        textTransform: 'none',
                                                    }}>
                                                    Remove
                                                </Button>
                                            </Box>
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                    color: theme[themeStyle].form_label,
                                                }}>
                                                {dateRange.reason}
                                            </Typography>
                                        </Box>
                                    ));
                                })()}
                            </Box>
                        </Stack>
                    </TabPanel>

                    {/* Media Tab */}
                    <TabPanel value={activeTab} index={4}>
                        <Stack spacing={3}>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                Media & Documents
                            </Typography>

                            {/* Media Kit Link */}
                            <Box>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].form_label,
                                        mb: 1,
                                    }}>
                                    Media Kit Link
                                </Typography>
                                <TextField
                                    label="Media Kit URL"
                                    fullWidth
                                    value={formData.media_kit_link || ''}
                                    onChange={e => handleInputChange('media_kit_link', e.target.value)}
                                    placeholder="https://example.com/media-kit"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LinkIcon sx={{ color: theme[themeStyle].form_label }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    {...commonTextFieldProps}
                                />
                                {formData.media_kit_link && (
                                    <Box sx={{ mt: 1 }}>
                                        <Link
                                            href={formData.media_kit_link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                color: theme[themeStyle].highlight,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                textDecoration: 'none',
                                                '&:hover': {
                                                    textDecoration: 'underline',
                                                },
                                            }}>
                                            View Media Kit
                                        </Link>
                                    </Box>
                                )}
                            </Box>

                            {/* Portfolio Links */}
                            <Box>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].form_label,
                                        mb: 1,
                                    }}>
                                    Portfolio Links
                                </Typography>

                                {/* Add new portfolio link */}
                                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                    <TextField
                                        label="Portfolio URL"
                                        fullWidth
                                        value={newPortfolioLink}
                                        onChange={e => setNewPortfolioLink(e.target.value)}
                                        placeholder="https://thspian.com/username"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <LinkIcon sx={{ color: theme[themeStyle].form_label }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        {...commonTextFieldProps}
                                    />
                                    <Button
                                        onClick={handleAddPortfolioLink}
                                        disabled={!newPortfolioLink.trim()}
                                        variant="outlined"
                                        sx={{
                                            borderColor: theme[themeStyle].highlight,
                                            color: theme[themeStyle].highlight,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            textTransform: 'none',
                                            minWidth: 'auto',
                                            px: 2,
                                            '&:hover': {
                                                borderColor: theme[themeStyle].highlight,
                                                backgroundColor: theme[themeStyle].form_divider,
                                            },
                                            '&:disabled': {
                                                borderColor: theme[themeStyle].form_divider,
                                                color: theme[themeStyle].placeholderGray,
                                            },
                                        }}>
                                        <AddIcon />
                                    </Button>
                                </Box>

                                {/* Display existing portfolio links */}
                                {(formData.portfolio || []).length > 0 && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {(formData.portfolio || []).map((link, index) => (
                                            <Box
                                                key={index}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    p: 2,
                                                    border: `1px solid ${theme[themeStyle].form_divider}`,
                                                    borderRadius: 1,
                                                    backgroundColor: theme[themeStyle].form_background,
                                                }}>
                                                <Link
                                                    href={link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        color: theme[themeStyle].highlight,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        textDecoration: 'none',
                                                        flex: 1,
                                                        '&:hover': {
                                                            textDecoration: 'underline',
                                                        },
                                                    }}>
                                                    {link}
                                                </Link>
                                                <IconButton
                                                    onClick={() => handleRemovePortfolioLink(link)}
                                                    size="small"
                                                    sx={{
                                                        color: theme[themeStyle].primary_red,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].form_divider,
                                                        },
                                                    }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>

                            {/* Documents Upload */}
                            <Box>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].form_label,
                                        mb: 1,
                                    }}>
                                    Documents
                                </Typography>

                                {/* File upload input */}
                                <Box sx={{ mb: 2 }}>
                                    <input
                                        type="file"
                                        multiple
                                        onChange={handleDocumentUpload}
                                        style={{ display: 'none' }}
                                        id="document-upload"
                                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                                    />
                                    <label htmlFor="document-upload">
                                        <Button
                                            component="span"
                                            variant="outlined"
                                            sx={{
                                                borderColor: theme[themeStyle].highlight,
                                                color: theme[themeStyle].highlight,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                textTransform: 'none',
                                                '&:hover': {
                                                    borderColor: theme[themeStyle].highlight,
                                                    backgroundColor: theme[themeStyle].form_divider,
                                                },
                                            }}>
                                            <AddIcon sx={{ mr: 1 }} />
                                            Upload Documents
                                        </Button>
                                    </label>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: 'block',
                                            mt: 1,
                                            color: theme[themeStyle].placeholderGray,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        }}>
                                        Supported formats: PDF, DOC, DOCX, JPG, PNG, GIF
                                    </Typography>
                                </Box>

                                {/* Display uploaded documents */}
                                {uploadedDocuments.length > 0 && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {uploadedDocuments.map((file, index) => (
                                            <Box
                                                key={index}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    p: 2,
                                                    border: `1px solid ${theme[themeStyle].form_divider}`,
                                                    borderRadius: 1,
                                                    mb: 1,
                                                }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <LinkIcon sx={{ color: theme[themeStyle].form_label }} />
                                                    <Box>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: theme[themeStyle].primary_white,
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            }}>
                                                            {file.name}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: theme[themeStyle].placeholderGray,
                                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                            }}>
                                                            {file.size
                                                                ? `${(file.size / 1024 / 1024).toFixed(2)} MB`
                                                                : file.format || 'Unknown type'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <IconButton
                                                    onClick={() => handleRemoveDocument(index)}
                                                    sx={{
                                                        color: theme[themeStyle].primary_red,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].form_divider,
                                                        },
                                                    }}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Box>
                                )}

                                {/* Display existing documents */}
                                {(formData.documents || []).length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                color: theme[themeStyle].form_label,
                                                mb: 1,
                                            }}>
                                            Existing Documents
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            {(formData.documents || []).map((doc, index) => (
                                                <Box
                                                    key={index}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        p: 2,
                                                        border: `1px solid ${theme[themeStyle].form_divider}`,
                                                        borderRadius: 1,
                                                        backgroundColor: theme[themeStyle].form_background,
                                                    }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                color: theme[themeStyle].primary_white,
                                                                mb: 0.5,
                                                            }}>
                                                            {doc.name}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: theme[themeStyle].placeholderGray,
                                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                            }}>
                                                            {doc.format}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Link
                                                            href={doc.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            sx={{
                                                                color: theme[themeStyle].highlight,
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                textDecoration: 'none',
                                                                '&:hover': {
                                                                    textDecoration: 'underline',
                                                                },
                                                            }}>
                                                            View
                                                        </Link>
                                                        <Button
                                                            onClick={() => {
                                                                const currentDocs = formData.documents || [];
                                                                handleInputChange(
                                                                    'documents',
                                                                    currentDocs.filter((_, i) => i !== index),
                                                                );
                                                            }}
                                                            size="small"
                                                            sx={{
                                                                color: theme[themeStyle].primary_red,
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                textTransform: 'none',
                                                                minWidth: 'auto',
                                                                p: 0,
                                                            }}>
                                                            Remove
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Stack>
                    </TabPanel>

                    <TabPanel value={activeTab} index={5}>
                        <Stack spacing={3}>
                            <Stack>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: theme[themeStyle].primary_white,
                                    }}>
                                    Social Platform Information
                                </Typography>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].primary_white,
                                        fontSize: 10,
                                    }}>
                                    Adjusting your social data is restricted. Please contact admin to make any changes
                                    here. However, you can adjust your rate card.
                                </Typography>
                            </Stack>

                            {/* Instagram */}
                            <Box>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: theme[themeStyle].highlight,
                                        mb: 2,
                                    }}>
                                    Instagram
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Username"
                                            fullWidth
                                            value={formData.instagram?.username || ''}
                                            disabled
                                            onChange={e =>
                                                handleInputChange('instagram', {
                                                    ...formData.instagram,
                                                    username: e.target.value,
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LockIcon
                                                            sx={{
                                                                color: theme[themeStyle].placeholderGray,
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Followers"
                                            fullWidth
                                            disabled
                                            type="number"
                                            value={formData.instagram?.followers || ''}
                                            onChange={e =>
                                                handleInputChange('instagram', {
                                                    ...formData.instagram,
                                                    followers: Number(e.target.value),
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LockIcon
                                                            sx={{
                                                                color: theme[themeStyle].placeholderGray,
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Engagement Rate"
                                            fullWidth
                                            disabled
                                            type="number"
                                            value={formData.instagram?.engagementRate || ''}
                                            onChange={e =>
                                                handleInputChange('instagram', {
                                                    ...formData.instagram,
                                                    engagementRate: Number(e.target.value),
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Typography
                                                                sx={{
                                                                    color: theme[themeStyle].placeholderGray,
                                                                    fontSize: 14,
                                                                }}>
                                                                %
                                                            </Typography>
                                                            <LockIcon
                                                                sx={{
                                                                    color: theme[themeStyle].placeholderGray,
                                                                    fontSize: 16,
                                                                }}
                                                            />
                                                        </Box>
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Average Likes"
                                            fullWidth
                                            disabled
                                            type="number"
                                            value={formData.instagram?.avg_likes || ''}
                                            onChange={e =>
                                                handleInputChange('instagram', {
                                                    ...formData.instagram,
                                                    avg_likes: Number(e.target.value),
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LockIcon
                                                            sx={{
                                                                color: theme[themeStyle].placeholderGray,
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Average Comments"
                                            fullWidth
                                            disabled
                                            type="number"
                                            value={formData.instagram?.avg_comments || ''}
                                            onChange={e =>
                                                handleInputChange('instagram', {
                                                    ...formData.instagram,
                                                    avg_comments: Number(e.target.value),
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LockIcon
                                                            sx={{
                                                                color: theme[themeStyle].placeholderGray,
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Profile Link"
                                            fullWidth
                                            disabled
                                            value={formData.instagram?.profile_link || ''}
                                            onChange={e =>
                                                handleInputChange('instagram', {
                                                    ...formData.instagram,
                                                    profile_link: e.target.value,
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LockIcon
                                                            sx={{
                                                                color: theme[themeStyle].placeholderGray,
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* YouTube */}
                            <Box sx={{ mt: 4 }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: theme[themeStyle].highlight,
                                        mb: 2,
                                    }}>
                                    YouTube
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Username"
                                            fullWidth
                                            value={formData.youtube?.username || ''}
                                            disabled
                                            onChange={e =>
                                                handleInputChange('youtube', {
                                                    ...formData.youtube,
                                                    username: e.target.value,
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LockIcon
                                                            sx={{
                                                                color: theme[themeStyle].placeholderGray,
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Subscribers"
                                            fullWidth
                                            disabled
                                            type="number"
                                            value={formData.youtube?.followers || ''}
                                            onChange={e =>
                                                handleInputChange('youtube', {
                                                    ...formData.youtube,
                                                    followers: Number(e.target.value),
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LockIcon
                                                            sx={{
                                                                color: theme[themeStyle].placeholderGray,
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Engagement Rate"
                                            fullWidth
                                            disabled
                                            type="number"
                                            value={formData.youtube?.engagementRate || ''}
                                            onChange={e =>
                                                handleInputChange('youtube', {
                                                    ...formData.youtube,
                                                    engagementRate: Number(e.target.value),
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Typography
                                                                sx={{
                                                                    color: theme[themeStyle].placeholderGray,
                                                                    fontSize: 14,
                                                                }}>
                                                                %
                                                            </Typography>
                                                            <LockIcon
                                                                sx={{
                                                                    color: theme[themeStyle].placeholderGray,
                                                                    fontSize: 16,
                                                                }}
                                                            />
                                                        </Box>
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Average Likes"
                                            fullWidth
                                            disabled
                                            type="number"
                                            value={formData.youtube?.avg_likes || ''}
                                            onChange={e =>
                                                handleInputChange('youtube', {
                                                    ...formData.youtube,
                                                    avg_likes: Number(e.target.value),
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LockIcon
                                                            sx={{
                                                                color: theme[themeStyle].placeholderGray,
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Average Comments"
                                            fullWidth
                                            disabled
                                            type="number"
                                            value={formData.youtube?.avg_comments || ''}
                                            onChange={e =>
                                                handleInputChange('youtube', {
                                                    ...formData.youtube,
                                                    avg_comments: Number(e.target.value),
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LockIcon
                                                            sx={{
                                                                color: theme[themeStyle].placeholderGray,
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Profile Link"
                                            fullWidth
                                            disabled
                                            value={formData.youtube?.profile_link || ''}
                                            onChange={e =>
                                                handleInputChange('youtube', {
                                                    ...formData.youtube,
                                                    profile_link: e.target.value,
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LockIcon
                                                            sx={{
                                                                color: theme[themeStyle].placeholderGray,
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* TikTok */}
                            <Box sx={{ mt: 4 }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: theme[themeStyle].highlight,
                                        mb: 2,
                                    }}>
                                    TikTok
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Username"
                                            fullWidth
                                            value={formData.tiktok?.username || ''}
                                            disabled
                                            onChange={e =>
                                                handleInputChange('tiktok', {
                                                    ...formData.tiktok,
                                                    username: e.target.value,
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LockIcon
                                                            sx={{
                                                                color: theme[themeStyle].placeholderGray,
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Followers"
                                            fullWidth
                                            disabled
                                            type="number"
                                            value={formData.tiktok?.followers || ''}
                                            onChange={e =>
                                                handleInputChange('tiktok', {
                                                    ...formData.tiktok,
                                                    followers: Number(e.target.value),
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LockIcon
                                                            sx={{
                                                                color: theme[themeStyle].placeholderGray,
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Engagement Rate"
                                            fullWidth
                                            disabled
                                            type="number"
                                            value={formData.tiktok?.engagementRate || ''}
                                            onChange={e =>
                                                handleInputChange('tiktok', {
                                                    ...formData.tiktok,
                                                    engagementRate: Number(e.target.value),
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Typography
                                                                sx={{
                                                                    color: theme[themeStyle].placeholderGray,
                                                                    fontSize: 14,
                                                                }}>
                                                                %
                                                            </Typography>
                                                            <LockIcon
                                                                sx={{
                                                                    color: theme[themeStyle].placeholderGray,
                                                                    fontSize: 16,
                                                                }}
                                                            />
                                                        </Box>
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Average Likes"
                                            fullWidth
                                            disabled
                                            type="number"
                                            value={formData.tiktok?.avg_likes || ''}
                                            onChange={e =>
                                                handleInputChange('tiktok', {
                                                    ...formData.tiktok,
                                                    avg_likes: Number(e.target.value),
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LockIcon
                                                            sx={{
                                                                color: theme[themeStyle].placeholderGray,
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Average Comments"
                                            fullWidth
                                            disabled
                                            type="number"
                                            value={formData.tiktok?.avg_comments || ''}
                                            onChange={e =>
                                                handleInputChange('tiktok', {
                                                    ...formData.tiktok,
                                                    avg_comments: Number(e.target.value),
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LockIcon
                                                            sx={{
                                                                color: theme[themeStyle].placeholderGray,
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Profile Link"
                                            fullWidth
                                            disabled
                                            value={formData.tiktok?.profile_link || ''}
                                            onChange={e =>
                                                handleInputChange('tiktok', {
                                                    ...formData.tiktok,
                                                    profile_link: e.target.value,
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LockIcon
                                                            sx={{
                                                                color: theme[themeStyle].placeholderGray,
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Facebook */}
                            <Box sx={{ mt: 4 }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: theme[themeStyle].highlight,
                                        mb: 2,
                                    }}>
                                    Facebook
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Username"
                                            fullWidth
                                            value={formData.facebook?.username || ''}
                                            disabled
                                            onChange={e =>
                                                handleInputChange('facebook', {
                                                    ...formData.facebook,
                                                    username: e.target.value,
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LockIcon
                                                            sx={{
                                                                color: theme[themeStyle].placeholderGray,
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Followers"
                                            fullWidth
                                            disabled
                                            type="number"
                                            value={formData.facebook?.followers || ''}
                                            onChange={e =>
                                                handleInputChange('facebook', {
                                                    ...formData.facebook,
                                                    followers: Number(e.target.value),
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LockIcon
                                                            sx={{
                                                                color: theme[themeStyle].placeholderGray,
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Engagement Rate"
                                            fullWidth
                                            disabled
                                            type="number"
                                            value={formData.facebook?.engagementRate || ''}
                                            onChange={e =>
                                                handleInputChange('facebook', {
                                                    ...formData.facebook,
                                                    engagementRate: Number(e.target.value),
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Typography
                                                                sx={{
                                                                    color: theme[themeStyle].placeholderGray,
                                                                    fontSize: 14,
                                                                }}>
                                                                %
                                                            </Typography>
                                                            <LockIcon
                                                                sx={{
                                                                    color: theme[themeStyle].placeholderGray,
                                                                    fontSize: 16,
                                                                }}
                                                            />
                                                        </Box>
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Average Likes"
                                            fullWidth
                                            disabled
                                            type="number"
                                            value={formData.facebook?.avg_likes || ''}
                                            onChange={e =>
                                                handleInputChange('facebook', {
                                                    ...formData.facebook,
                                                    avg_likes: Number(e.target.value),
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LockIcon
                                                            sx={{
                                                                color: theme[themeStyle].placeholderGray,
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Average Comments"
                                            fullWidth
                                            disabled
                                            type="number"
                                            value={formData.facebook?.avg_comments || ''}
                                            onChange={e =>
                                                handleInputChange('facebook', {
                                                    ...formData.facebook,
                                                    avg_comments: Number(e.target.value),
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LockIcon
                                                            sx={{
                                                                color: theme[themeStyle].placeholderGray,
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Profile Link"
                                            fullWidth
                                            disabled
                                            value={formData.facebook?.profile_link || ''}
                                            onChange={e =>
                                                handleInputChange('facebook', {
                                                    ...formData.facebook,
                                                    profile_link: e.target.value,
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LockIcon
                                                            sx={{
                                                                color: theme[themeStyle].placeholderGray,
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Twitter */}
                            <Box sx={{ mt: 4 }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: theme[themeStyle].highlight,
                                        mb: 2,
                                    }}>
                                    Twitter
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Username"
                                            fullWidth
                                            value={formData.twitter?.username || ''}
                                            disabled
                                            onChange={e =>
                                                handleInputChange('twitter', {
                                                    ...formData.twitter,
                                                    username: e.target.value,
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LockIcon
                                                            sx={{
                                                                color: theme[themeStyle].placeholderGray,
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Followers"
                                            fullWidth
                                            disabled
                                            type="number"
                                            value={formData.twitter?.followers || ''}
                                            onChange={e =>
                                                handleInputChange('twitter', {
                                                    ...formData.twitter,
                                                    followers: Number(e.target.value),
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LockIcon
                                                            sx={{
                                                                color: theme[themeStyle].placeholderGray,
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Engagement Rate"
                                            fullWidth
                                            disabled
                                            type="number"
                                            value={formData.twitter?.engagementRate || ''}
                                            onChange={e =>
                                                handleInputChange('twitter', {
                                                    ...formData.twitter,
                                                    engagementRate: Number(e.target.value),
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Typography
                                                                sx={{
                                                                    color: theme[themeStyle].placeholderGray,
                                                                    fontSize: 14,
                                                                }}>
                                                                %
                                                            </Typography>
                                                            <LockIcon
                                                                sx={{
                                                                    color: theme[themeStyle].placeholderGray,
                                                                    fontSize: 16,
                                                                }}
                                                            />
                                                        </Box>
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Average Likes"
                                            fullWidth
                                            disabled
                                            type="number"
                                            value={formData.twitter?.avg_likes || ''}
                                            onChange={e =>
                                                handleInputChange('twitter', {
                                                    ...formData.twitter,
                                                    avg_likes: Number(e.target.value),
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LockIcon
                                                            sx={{
                                                                color: theme[themeStyle].placeholderGray,
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Average Comments"
                                            fullWidth
                                            disabled
                                            type="number"
                                            value={formData.twitter?.avg_comments || ''}
                                            onChange={e =>
                                                handleInputChange('twitter', {
                                                    ...formData.twitter,
                                                    avg_comments: Number(e.target.value),
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LockIcon
                                                            sx={{
                                                                color: theme[themeStyle].placeholderGray,
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Profile Link"
                                            fullWidth
                                            disabled
                                            value={formData.twitter?.profile_link || ''}
                                            onChange={e =>
                                                handleInputChange('twitter', {
                                                    ...formData.twitter,
                                                    profile_link: e.target.value,
                                                })
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LockIcon
                                                            sx={{
                                                                color: theme[themeStyle].placeholderGray,
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                ...commonTextFieldProps.sx,
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                    WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                    opacity: 0.5,
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                    {
                                                        borderColor: theme[themeStyle].form_divider,
                                                    },
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Social Platform Pricing */}
                            <Box sx={{ mt: 4 }}>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: theme[themeStyle].primary_white,
                                        mb: 2,
                                    }}>
                                    Content Pricing
                                </Typography>

                                {/* Instagram Pricing */}
                                <Accordion
                                    defaultExpanded
                                    sx={{
                                        backgroundColor: 'transparent',
                                        '&:before': { display: 'none' },
                                        '& .MuiAccordionSummary-root': {
                                            borderBottom: `1px solid ${theme[themeStyle].form_divider}`,
                                        },
                                    }}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon sx={{ color: theme[themeStyle].form_label }} />}>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                color: theme[themeStyle].highlight,
                                            }}>
                                            Instagram
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Stack spacing={2}>
                                            {CONTENT_CATEGORIES.map(category => {
                                                const defaultRange: [number, number] = [0, 100];
                                                const range = formData.instagram?.pricing?.[category] || defaultRange;
                                                const formatter = new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                    minimumFractionDigits: 0,
                                                    maximumFractionDigits: 0,
                                                });

                                                return (
                                                    <Box key={category}>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                mb: 1,
                                                            }}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                    color: theme[themeStyle].form_label,
                                                                    textTransform: 'capitalize',
                                                                }}>
                                                                {category.replace(/_/g, ' ')}
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                                    color: theme[themeStyle].highlight,
                                                                }}>
                                                                {formatter.format(range[0] ?? 0)} -{' '}
                                                                {formatter.format(range[1] ?? 100)}
                                                            </Typography>
                                                        </Box>
                                                        <Slider
                                                            value={range}
                                                            onChange={(_, value) =>
                                                                handleInputChange('instagram', {
                                                                    ...formData.instagram,
                                                                    pricing: {
                                                                        ...formData.instagram?.pricing,
                                                                        [category]: value as [number, number],
                                                                    },
                                                                })
                                                            }
                                                            valueLabelDisplay="auto"
                                                            valueLabelFormat={value => formatter.format(value)}
                                                            min={0}
                                                            max={10000}
                                                            step={25}
                                                            disableSwap
                                                            sx={{
                                                                '& .MuiSlider-track': {
                                                                    backgroundColor: theme[themeStyle].highlight,
                                                                },
                                                                '& .MuiSlider-rail': {
                                                                    backgroundColor: theme[themeStyle].form_divider,
                                                                },
                                                                '& .MuiSlider-thumb': {
                                                                    backgroundColor: theme[themeStyle].highlight,
                                                                    '&:hover': {
                                                                        backgroundColor: theme[themeStyle].highlight,
                                                                        opacity: 0.8,
                                                                    },
                                                                },
                                                                '& .MuiSlider-valueLabel': {
                                                                    backgroundColor: theme[themeStyle].highlight,
                                                                },
                                                            }}
                                                        />
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    </AccordionDetails>
                                </Accordion>

                                {/* YouTube Pricing */}
                                <Accordion
                                    sx={{
                                        backgroundColor: 'transparent',
                                        '&:before': { display: 'none' },
                                        '& .MuiAccordionSummary-root': {
                                            borderBottom: `1px solid ${theme[themeStyle].form_divider}`,
                                        },
                                    }}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon sx={{ color: theme[themeStyle].form_label }} />}>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                color: theme[themeStyle].highlight,
                                            }}>
                                            YouTube
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Stack spacing={2}>
                                            {CONTENT_CATEGORIES.map(category => {
                                                const defaultRange: [number, number] = [0, 100];
                                                const range = formData.youtube?.pricing?.[category] || defaultRange;
                                                const formatter = new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                    minimumFractionDigits: 0,
                                                    maximumFractionDigits: 0,
                                                });

                                                return (
                                                    <Box key={category}>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                mb: 1,
                                                            }}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                    color: theme[themeStyle].form_label,
                                                                    textTransform: 'capitalize',
                                                                }}>
                                                                {category.replace(/_/g, ' ')}
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                                    color: theme[themeStyle].highlight,
                                                                }}>
                                                                {formatter.format(range[0] ?? 0)} -{' '}
                                                                {formatter.format(range[1] ?? 100)}
                                                            </Typography>
                                                        </Box>
                                                        <Slider
                                                            value={range}
                                                            onChange={(_, value) =>
                                                                handleInputChange('youtube', {
                                                                    ...formData.youtube,
                                                                    pricing: {
                                                                        ...formData.youtube?.pricing,
                                                                        [category]: value as [number, number],
                                                                    },
                                                                })
                                                            }
                                                            valueLabelDisplay="auto"
                                                            valueLabelFormat={value => formatter.format(value)}
                                                            min={0}
                                                            max={10000}
                                                            step={25}
                                                            disableSwap
                                                            sx={{
                                                                '& .MuiSlider-track': {
                                                                    backgroundColor: theme[themeStyle].highlight,
                                                                },
                                                                '& .MuiSlider-rail': {
                                                                    backgroundColor: theme[themeStyle].form_divider,
                                                                },
                                                                '& .MuiSlider-thumb': {
                                                                    backgroundColor: theme[themeStyle].highlight,
                                                                    '&:hover': {
                                                                        backgroundColor: theme[themeStyle].highlight,
                                                                        opacity: 0.8,
                                                                    },
                                                                },
                                                                '& .MuiSlider-valueLabel': {
                                                                    backgroundColor: theme[themeStyle].highlight,
                                                                },
                                                            }}
                                                        />
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    </AccordionDetails>
                                </Accordion>

                                {/* TikTok Pricing */}
                                <Accordion
                                    sx={{
                                        backgroundColor: 'transparent',
                                        '&:before': { display: 'none' },
                                        '& .MuiAccordionSummary-root': {
                                            borderBottom: `1px solid ${theme[themeStyle].form_divider}`,
                                        },
                                    }}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon sx={{ color: theme[themeStyle].form_label }} />}>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                color: theme[themeStyle].highlight,
                                            }}>
                                            TikTok
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Stack spacing={2}>
                                            {CONTENT_CATEGORIES.map(category => {
                                                const defaultRange: [number, number] = [0, 100];
                                                const range = formData.tiktok?.pricing?.[category] || defaultRange;
                                                const formatter = new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                    minimumFractionDigits: 0,
                                                    maximumFractionDigits: 0,
                                                });

                                                return (
                                                    <Box key={category}>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                mb: 1,
                                                            }}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                    color: theme[themeStyle].form_label,
                                                                    textTransform: 'capitalize',
                                                                }}>
                                                                {category.replace(/_/g, ' ')}
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                                    color: theme[themeStyle].highlight,
                                                                }}>
                                                                {formatter.format(range[0] ?? 0)} -{' '}
                                                                {formatter.format(range[1] ?? 100)}
                                                            </Typography>
                                                        </Box>
                                                        <Slider
                                                            value={range}
                                                            onChange={(_, value) =>
                                                                handleInputChange('tiktok', {
                                                                    ...formData.tiktok,
                                                                    pricing: {
                                                                        ...formData.tiktok?.pricing,
                                                                        [category]: value as [number, number],
                                                                    },
                                                                })
                                                            }
                                                            valueLabelDisplay="auto"
                                                            valueLabelFormat={value => formatter.format(value)}
                                                            min={0}
                                                            max={10000}
                                                            step={25}
                                                            disableSwap
                                                            sx={{
                                                                '& .MuiSlider-track': {
                                                                    backgroundColor: theme[themeStyle].highlight,
                                                                },
                                                                '& .MuiSlider-rail': {
                                                                    backgroundColor: theme[themeStyle].form_divider,
                                                                },
                                                                '& .MuiSlider-thumb': {
                                                                    backgroundColor: theme[themeStyle].highlight,
                                                                    '&:hover': {
                                                                        backgroundColor: theme[themeStyle].highlight,
                                                                        opacity: 0.8,
                                                                    },
                                                                },
                                                                '& .MuiSlider-valueLabel': {
                                                                    backgroundColor: theme[themeStyle].highlight,
                                                                },
                                                            }}
                                                        />
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    </AccordionDetails>
                                </Accordion>

                                {/* Facebook Pricing */}
                                <Accordion
                                    sx={{
                                        backgroundColor: 'transparent',
                                        '&:before': { display: 'none' },
                                        '& .MuiAccordionSummary-root': {
                                            borderBottom: `1px solid ${theme[themeStyle].form_divider}`,
                                        },
                                    }}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon sx={{ color: theme[themeStyle].form_label }} />}>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                color: theme[themeStyle].highlight,
                                            }}>
                                            Facebook
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Stack spacing={2}>
                                            {CONTENT_CATEGORIES.map(category => {
                                                const defaultRange: [number, number] = [0, 100];
                                                const range = formData.facebook?.pricing?.[category] || defaultRange;
                                                const formatter = new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                    minimumFractionDigits: 0,
                                                    maximumFractionDigits: 0,
                                                });

                                                return (
                                                    <Box key={category}>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                mb: 1,
                                                            }}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                    color: theme[themeStyle].form_label,
                                                                    textTransform: 'capitalize',
                                                                }}>
                                                                {category.replace(/_/g, ' ')}
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                                    color: theme[themeStyle].highlight,
                                                                }}>
                                                                {formatter.format(range[0] ?? 0)} -{' '}
                                                                {formatter.format(range[1] ?? 100)}
                                                            </Typography>
                                                        </Box>
                                                        <Slider
                                                            value={range}
                                                            onChange={(_, value) =>
                                                                handleInputChange('facebook', {
                                                                    ...formData.facebook,
                                                                    pricing: {
                                                                        ...formData.facebook?.pricing,
                                                                        [category]: value as [number, number],
                                                                    },
                                                                })
                                                            }
                                                            valueLabelDisplay="auto"
                                                            valueLabelFormat={value => formatter.format(value)}
                                                            min={0}
                                                            max={10000}
                                                            step={25}
                                                            disableSwap
                                                            sx={{
                                                                '& .MuiSlider-track': {
                                                                    backgroundColor: theme[themeStyle].highlight,
                                                                },
                                                                '& .MuiSlider-rail': {
                                                                    backgroundColor: theme[themeStyle].form_divider,
                                                                },
                                                                '& .MuiSlider-thumb': {
                                                                    backgroundColor: theme[themeStyle].highlight,
                                                                    '&:hover': {
                                                                        backgroundColor: theme[themeStyle].highlight,
                                                                        opacity: 0.8,
                                                                    },
                                                                },
                                                                '& .MuiSlider-valueLabel': {
                                                                    backgroundColor: theme[themeStyle].highlight,
                                                                },
                                                            }}
                                                        />
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    </AccordionDetails>
                                </Accordion>

                                {/* Twitter Pricing */}
                                <Accordion
                                    sx={{
                                        backgroundColor: 'transparent',
                                        '&:before': { display: 'none' },
                                        '& .MuiAccordionSummary-root': {
                                            borderBottom: `1px solid ${theme[themeStyle].form_divider}`,
                                        },
                                    }}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon sx={{ color: theme[themeStyle].form_label }} />}>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                color: theme[themeStyle].highlight,
                                            }}>
                                            Twitter
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Stack spacing={2}>
                                            {CONTENT_CATEGORIES.map(category => {
                                                const defaultRange: [number, number] = [0, 100];
                                                const range = formData.twitter?.pricing?.[category] || defaultRange;
                                                const formatter = new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                    minimumFractionDigits: 0,
                                                    maximumFractionDigits: 0,
                                                });

                                                return (
                                                    <Box key={category}>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                mb: 1,
                                                            }}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                    color: theme[themeStyle].form_label,
                                                                    textTransform: 'capitalize',
                                                                }}>
                                                                {category.replace(/_/g, ' ')}
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                                    color: theme[themeStyle].highlight,
                                                                }}>
                                                                {formatter.format(range[0] ?? 0)} -{' '}
                                                                {formatter.format(range[1] ?? 100)}
                                                            </Typography>
                                                        </Box>
                                                        <Slider
                                                            value={range}
                                                            onChange={(_, value) =>
                                                                handleInputChange('twitter', {
                                                                    ...formData.twitter,
                                                                    pricing: {
                                                                        ...formData.twitter?.pricing,
                                                                        [category]: value as [number, number],
                                                                    },
                                                                })
                                                            }
                                                            valueLabelDisplay="auto"
                                                            valueLabelFormat={value => formatter.format(value)}
                                                            min={0}
                                                            max={10000}
                                                            step={25}
                                                            disableSwap
                                                            sx={{
                                                                '& .MuiSlider-track': {
                                                                    backgroundColor: theme[themeStyle].highlight,
                                                                },
                                                                '& .MuiSlider-rail': {
                                                                    backgroundColor: theme[themeStyle].form_divider,
                                                                },
                                                                '& .MuiSlider-thumb': {
                                                                    backgroundColor: theme[themeStyle].highlight,
                                                                    '&:hover': {
                                                                        backgroundColor: theme[themeStyle].highlight,
                                                                        opacity: 0.8,
                                                                    },
                                                                },
                                                                '& .MuiSlider-valueLabel': {
                                                                    backgroundColor: theme[themeStyle].highlight,
                                                                },
                                                            }}
                                                        />
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    </AccordionDetails>
                                </Accordion>
                            </Box>
                        </Stack>
                    </TabPanel>
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    borderTop: `1px solid ${theme[themeStyle].form_divider}`,
                    p: 2,
                    gap: 1,
                }}>
                <CustomButton onClick={onClose} label="Cancel" buttonProps={{ variant: 'outlined' }} />
                <CustomButton onClick={handleSave} label="Save Changes" disabled={!hasAnyChanges()} />
            </DialogActions>
        </Dialog>
    );
};
